import React from "react";
import useSWR from "swr";
import api, { money, fmtDate } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import { Link } from "react-router-dom";
import { TrendingUp, TrendingDown, DollarSign, Users, AlertTriangle, Sparkles } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LineChart, Line } from "recharts";
import ToursCalendar from "@/components/ToursCalendar";

const fetcher = (url) => api.get(url).then((r) => r.data);

function KpiCard({ label, value, icon: Icon, tone = "slate", sub }) {
  const tones = {
    slate: "text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800/50",
    green: "text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-950",
    red: "text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-950",
    blue: "text-sky-700 dark:text-sky-300 bg-sky-100 dark:bg-sky-950",
    amber: "text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-950",
  };
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest mono text-slate-500">{label}</div>
            <div className="text-2xl font-bold mt-2">{value}</div>
            {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
          </div>
          <div className={`p-2.5 rounded-lg ${tones[tone]}`}><Icon size={18} /></div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { data, error } = useSWR("/dashboard", fetcher);
  const { data: allTours } = useSWR("/tours", fetcher);

  if (error) return <div className="text-red-600">Error cargando dashboard</div>;
  if (!data) return <div className="text-slate-500">Cargando dashboard…</div>;

  const k = data.kpis;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Dashboard General</h1>
          <p className="text-slate-500 mt-1">Vista global · Moneda base <span className="mono">{data.base_currency}</span></p>
        </div>
      </div>

      <ToursCalendar tours={allTours || []} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Ingresos previstos" value={money(k.total_expected)} icon={TrendingUp} tone="green" />
        <KpiCard label="Cobrado" value={money(k.total_collected)} icon={DollarSign} tone="blue" sub={`Pendiente: ${money(k.total_pending)}`} />
        <KpiCard label="Gastos estimados" value={money(k.expenses_budget)} icon={TrendingDown} tone="amber" />
        <KpiCard label="Beneficio previsto" value={money(k.profit_expected)} icon={Sparkles} tone={k.profit_expected >= 0 ? "green" : "red"} />
        <KpiCard label="Participantes confirmados" value={k.participants_confirmed} icon={Users} tone="green" />
        <KpiCard label="Reservas pendientes" value={k.participants_pending} icon={AlertTriangle} tone="amber" />
        <KpiCard label="Tours activos" value={data.active_tours.length} icon={Sparkles} tone="blue" />
        <KpiCard label="Próximos tours" value={data.upcoming_tours.length} icon={TrendingUp} tone="slate" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle>Ingresos vs. Gastos por mes</CardTitle></CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="revenue" fill="#026FCB" name="Ingresos" radius={[4,4,0,0]} />
                <Bar dataKey="expenses" fill="#F59E0B" name="Gastos" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Ocupación por tour</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {data.seats.map((s) => (
              <Link key={s.id} to={`/tours/${s.id}`} className="block group" data-testid={`dashboard-seat-${s.id}`}>
                <div className="flex items-center justify-between text-sm">
                  <div className="truncate font-medium group-hover:text-[#026FCB]">{s.name}</div>
                  <StatusBadge value={s.status} />
                </div>
                <div className="mt-1.5 h-2 rounded-full bg-slate-100 dark:bg-[#1A2336] overflow-hidden">
                  <div
                    className="h-full bg-[#026FCB]"
                    style={{ width: `${Math.min(100, (s.confirmed / (s.max_clients || 1)) * 100)}%` }}
                  />
                </div>
                <div className="text-xs text-slate-500 mt-1">{s.confirmed}/{s.max_clients} plazas · {s.available} disponibles</div>
              </Link>
            ))}
            {data.seats.length === 0 && <div className="text-slate-500 text-sm">Sin tours aún.</div>}
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader><CardTitle>Tareas próximos 7 días</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {data.tasks_7.map((t) => (
              <div key={t.id} data-testid={`dash-task-${t.id}`} className="flex items-center justify-between text-sm">
                <div className="truncate">{t.title}</div>
                <StatusBadge value={t.priority} />
              </div>
            ))}
            {data.tasks_7.length === 0 && <div className="text-slate-500 text-sm">Sin tareas en los próximos 7 días.</div>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Tareas 15 días</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {data.tasks_15.map((t) => (<div key={t.id} className="text-sm truncate">• {t.title}</div>))}
            {data.tasks_15.length === 0 && <div className="text-slate-500 text-sm">—</div>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Tareas 30 días</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {data.tasks_30.map((t) => (<div key={t.id} className="text-sm truncate">• {t.title}</div>))}
            {data.tasks_30.length === 0 && <div className="text-slate-500 text-sm">—</div>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
